/**
 * KHIYARSA (AI): local EN/AR strings for the Create-with-AI dialog — same
 * pattern as the KMD and cloud modules (upstream AI localization is
 * deliberately not used; its Arabic coverage is empty).
 */
import { useAppLangCode } from "../../app-language/language-state";

const en = {
  createWithAi: "Create with AI…",
  dialogTitle: "Create diagram with AI",
  promptLabel: "Describe what you want",
  promptPlaceholder:
    "e.g. A checkout flow: the customer adds items, pays, the order service validates payment and either ships or notifies the customer",
  diagramType: "Diagram type",
  typeAuto: "Auto",
  language: "Labels",
  langAuto: "Match my description",
  langEn: "English",
  langAr: "Arabic",
  detail: "Detail",
  detailConcise: "Concise",
  detailDetailed: "Detailed",
  useIcons: "Suggest icons",
  useSelection: "Use selected text as source material",
  privacyNote:
    "Your description (and source material, if attached) is sent to the configured AI provider when you press Generate. Nothing is sent before that, and nothing is stored on the server.",
  generate: "Generate",
  cancel: "Cancel",
  stageGenerating: "Generating…",
  stageValidating: "Validating…",
  stageRepairing: "Repairing…",
  attempt: "attempt",
  resultValid: "Diagram ready.",
  resultInvalid:
    "The AI could not produce fully valid KMD. You can fix the remaining issues below, regenerate, or edit the KMD by hand.",
  errCancelled: "Generation cancelled.",
  errTimeout: "The AI provider timed out. Try again.",
  errProvider: "The AI provider returned an error. Try again in a moment.",
  errProviderBusy: "The AI provider is busy. Try again in a moment.",
  errRateLimited: "Slow down a little — you're generating too quickly.",
  errQuota: "You've reached today's generation limit.",
  errBudget: "AI generation is paused for today (site-wide limit reached).",
  errNotConfigured: "AI generation is not configured on this server.",
  errAuth: "Sign in to use AI generation.",
  errGeneric: "Something went wrong. Try again.",
  insert: "Insert diagram",
  regenerate: "Regenerate",
  editKmd: "Edit KMD",
  showKmd: "Show KMD",
  hideKmd: "Hide KMD",
  copyKmd: "Copy KMD",
  copied: "Copied.",
  downloadMd: "Download .md",
  back: "Back",
  problems: "Diagnostics",
  line: "line",
  noPreview: "Preview appears when the document compiles without errors.",
  sourceAttached: "Source material attached",
  chars: "characters",
};

const ar: typeof en = {
  createWithAi: "إنشاء بالذكاء الاصطناعي…",
  dialogTitle: "إنشاء مخطط بالذكاء الاصطناعي",
  promptLabel: "صِف ما تريد",
  promptPlaceholder:
    "مثال: مسار الشراء: يضيف العميل المنتجات، يدفع، تتحقق خدمة الطلبات من الدفع ثم تشحن أو تُخطر العميل",
  diagramType: "نوع المخطط",
  typeAuto: "تلقائي",
  language: "لغة التسميات",
  langAuto: "بلغة وصفي",
  langEn: "الإنجليزية",
  langAr: "العربية",
  detail: "التفصيل",
  detailConcise: "موجز",
  detailDetailed: "مفصّل",
  useIcons: "اقتراح أيقونات",
  useSelection: "استخدام النص المحدد كمصدر",
  privacyNote:
    "يُرسل وصفك (والمصدر المرفق إن وُجد) إلى مزوّد الذكاء الاصطناعي عند الضغط على «إنشاء». لا يُرسل شيء قبل ذلك، ولا يُخزَّن شيء على الخادم.",
  generate: "إنشاء",
  cancel: "إلغاء",
  stageGenerating: "جارٍ الإنشاء…",
  stageValidating: "جارٍ التحقق…",
  stageRepairing: "جارٍ الإصلاح…",
  attempt: "محاولة",
  resultValid: "المخطط جاهز.",
  resultInvalid:
    "لم يتمكن الذكاء الاصطناعي من إنتاج KMD صالح بالكامل. يمكنك إصلاح الملاحظات المتبقية أدناه، أو إعادة الإنشاء، أو تحرير KMD يدويًا.",
  errCancelled: "أُلغي الإنشاء.",
  errTimeout: "انتهت مهلة مزوّد الذكاء الاصطناعي. حاول مجددًا.",
  errProvider: "أعاد مزوّد الذكاء الاصطناعي خطأً. حاول بعد قليل.",
  errProviderBusy: "مزوّد الذكاء الاصطناعي مشغول. حاول بعد قليل.",
  errRateLimited: "تمهّل قليلًا — أنت تُنشئ بسرعة كبيرة.",
  errQuota: "بلغت حدّ الإنشاء اليومي.",
  errBudget: "أُوقف الإنشاء مؤقتًا اليوم (بلوغ حدّ الموقع الكلي).",
  errNotConfigured: "الإنشاء بالذكاء الاصطناعي غير مُفعّل على هذا الخادم.",
  errAuth: "سجّل الدخول لاستخدام الإنشاء بالذكاء الاصطناعي.",
  errGeneric: "حدث خطأ ما. حاول مجددًا.",
  insert: "إدراج المخطط",
  regenerate: "إعادة الإنشاء",
  editKmd: "تحرير KMD",
  showKmd: "إظهار KMD",
  hideKmd: "إخفاء KMD",
  copyKmd: "نسخ KMD",
  copied: "تم النسخ.",
  downloadMd: "تنزيل ‎.md",
  back: "رجوع",
  problems: "الملاحظات",
  line: "سطر",
  noPreview: "تظهر المعاينة عندما يُترجم المستند دون أخطاء.",
  sourceAttached: "المصدر مرفق",
  chars: "حرفًا",
};

export const useAiT = () => {
  const [langCode] = useAppLangCode();
  const dict = langCode.startsWith("ar") ? ar : en;
  return (key: keyof typeof en) => dict[key];
};
